import {
  Box,
  Flex,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
} from '@chakra-ui/react';
import { memo, useState } from 'react';
import { Field, ErrorMessage, useFormikContext } from 'formik';
import Countdown from 'react-countdown';

import { useLoginManager } from './login-manager';
import { useGenerateOtpMutation } from '@/store/query/auth';

const RequestOtp = memo(function RequestOtp() {
  const [generateOtp, { isLoading }] = useGenerateOtpMutation();
  const [enableResend, setResend] = useState(false);
  const { email } = useLoginManager();

  const renderer = ({ seconds }) => {
    return <span>{seconds} sec</span>;
  };

  const onResendOtp = async () => {
    try {
      await generateOtp({ email });
      setResend(false);
    } catch {
      // generateOtp surfaces its own error; nothing further to handle here
    }
  };

  if (enableResend) {
    return (
      <Button
        variant="secondary"
        onClick={onResendOtp}
        isLoading={isLoading}
        data-test-id="resend-otp"
      >
        Request OTP
      </Button>
    );
  }

  return (
    <Button
      data-test-id="resend-otp-disabled"
      variant="secondary"
      onClick={onResendOtp}
      isDisabled={true}
      minW="200px"
    >
      Request OTP in
      <Text ml="5px">
        <Countdown
          // Countdown needs a wall-clock target; reading the clock here is
          // intentional. Left as-is to avoid behaviour changes in this legacy
          // component during the data-test-id migration.
          // eslint-disable-next-line react-hooks/purity
          date={Date.now() + 59000}
          renderer={renderer}
          onComplete={() => {
            setResend(true);
          }}
        />
      </Text>
    </Button>
  );
});

const VerifyOtp = () => {
  return (
    <Field name="otp" data-test-id="verify-otp">
      {({ field, form }) => (
        <FormControl
          isRequired
          isInvalid={form.errors.otp && form.touched.otp}
          mb="16px"
        >
          <Flex alignItems="end">
            <Box flex="1">
              <FormLabel fontWeight="semiBold">Enter OTP code</FormLabel>
              <Input {...field} id="otp" placeholder="*******" />
            </Box>
            <Box ml="12px">
              <RequestOtp />
            </Box>
          </Flex>
          <FormErrorMessage>
            <ErrorMessage name="otp" />
          </FormErrorMessage>
        </FormControl>
      )}
    </Field>
  );
};

export const VerifyUser = () => {
  const [generateOtp, { isLoading }] = useGenerateOtpMutation();
  const { email } = useLoginManager();
  const [isOtpSent, setOtpSent] = useState(false);
  const { setErrors } = useFormikContext();
  const onGenerateOtp = async () => {
    try {
      await generateOtp({ email });
      setErrors({});
      setOtpSent(true);
    } catch {
      // generateOtp surfaces its own error; nothing further to handle here
    }
  };
  if (isOtpSent) {
    return <VerifyOtp />;
  }
  return (
    <>
      <Text size="sm-medium" fontSize="14px" mb="8px">
        Hit the button below and we&apos;ll send you the verification code on
        email
      </Text>
      <Button
        data-test-id="generate-otp"
        width="full"
        mb="16px"
        variant="secondary"
        onClick={onGenerateOtp}
        isLoading={isLoading}
      >
        Request OTP code
      </Button>
    </>
  );
};
